import React, { useState, useEffect } from "react";
import { Spin, message } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { EmailVerification } from "../pages/EmailVerification";
import { UserLegalAcceptanceModal } from "./UserLegalAcceptanceModal";
import apiClient from "../utils/axios";

interface EmailVerificationControlProps {
  children: React.ReactNode;
}

interface EmailVerificationStatus {
  email_verified: boolean;
  needs_verification: boolean;
  email: string;
}

interface UserLegalStatus {
  legal_acceptance_required: boolean;
  user_id: string;
}

export const EmailVerificationControl: React.FC<EmailVerificationControlProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const [verificationStatus, setVerificationStatus] = useState<EmailVerificationStatus | null>(null);
  const [legalStatus, setLegalStatus] = useState<UserLegalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUserLegalModal, setShowUserLegalModal] = useState(false);

  const checkEmailVerification = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Get the Auth0 token
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get("/api/access/email-verification", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setVerificationStatus(response.data);
    } catch (error) {
      console.error("Error checking email verification:", error);
      message.error("Failed to check email verification status");
      // Default to requiring verification on error
      setVerificationStatus({
        email_verified: false,
        needs_verification: true,
        email: ""
      });
    } finally {
      setLoading(false);
    }
  };

  const checkUserLegalAcceptance = async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get("/api/user-legal/required", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setLegalStatus(response.data);
      
      // If legal acceptance is required, show the modal
      if (response.data.legal_acceptance_required) {
        setShowUserLegalModal(true);
      }
    } catch (error) {
      console.error("Error checking user legal acceptance:", error);
      // Default to requiring legal acceptance on error
      setLegalStatus({
        legal_acceptance_required: true,
        user_id: ""
      });
      setShowUserLegalModal(true);
    }
  };

  useEffect(() => {
    if (!auth0Loading && isAuthenticated) {
      checkEmailVerification();
    } else if (!auth0Loading && !isAuthenticated) {
      setLoading(false);
    }
  }, [auth0Loading, isAuthenticated]);

  // Check user legal acceptance after email verification is confirmed
  useEffect(() => {
    if (verificationStatus && verificationStatus.email_verified && !verificationStatus.needs_verification) {
      checkUserLegalAcceptance();
    }
  }, [verificationStatus]);

  const handleUserLegalAcceptanceSuccess = () => {
    setShowUserLegalModal(false);
    setLegalStatus({
      legal_acceptance_required: false,
      user_id: legalStatus?.user_id || ""
    });
    message.success("Legal documents accepted successfully!");
  };

  if (auth0Loading || loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // If not authenticated, let the children handle it
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If email verification is needed, show the verification page
  if (verificationStatus?.needs_verification) {
    return <EmailVerification />;
  }

  // If email is verified but user legal acceptance is required, show the legal modal
  if (verificationStatus?.email_verified && showUserLegalModal) {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#f5f5f5",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <h2>Welcome to CodePilot!</h2>
            <p>Please accept our legal documents to continue.</p>
          </div>
        </div>
        <UserLegalAcceptanceModal
          open={showUserLegalModal}
          onSuccess={handleUserLegalAcceptanceSuccess}
        />
      </>
    );
  }

  // If email is verified and legal documents are accepted, proceed to children
  return <>{children}</>;
}; 